import css from '@styled-system/css';
import styled from 'styled-components';
import { compose, border, space, typography, variant, alignSelf } from 'styled-system';
import theme from '../theme';
import { getHoverColor, getClickColor } from '../utils';
import { themeGet } from '@styled-system/theme-get';
function buttonStyles(props) {
    const size = props.size || 'medium';
    const buttonSizes = {
        large: {
            borderWidth: '2px',
            fontSize: '1',
            lineHeight: '1.15',
            padding: '3'
        },
        medium: {
            borderWidth: '1px',
            fontSize: '0',
            lineHeight: '1.25',
            padding: '2'
        },
        small: {
            borderWidth: '1px',
            fontSize: '12px',
            lineHeight: '1.25',
            padding: '1'
        }
    };
    return css(buttonSizes[size]);
}
const Button = styled.button `
  ${css({
    // Base button styles
    alignSelf: 'flex-start',
    cursor: 'pointer',
    fontFamily: 'body',
    fontSize: '0',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    borderStyle: 'solid',
    borderRadius: '1',
    ':focus': {
        boxShadow: `0 0 1px 1px ${themeGet('colors.gray.2', 'lightgray')({ theme })}`,
        outline: '0'
    }
})}

  ${({ theme: { borderWidths, buttons }, color }) => {
    const { bg, border, color: textColor } = buttons.default;
    return css({
        // Default button styles
        bg: bg.default,
        borderColor: color
            ? themeGet('colors.' + color, color)({ theme })
            : border.default,
        color: color || textColor.default,
        // Default hover styles
        '&:hover': {
            bg: color
                ? getHoverColor({ color, variant: 'secondary', theme })
                : bg.hover,
            borderColor: color
                ? getHoverColor({ color, variant: 'secondary', theme })
                : border.hover,
            color: textColor.hover
        },
        // Default active styles
        '&:active': {
            bg: color
                ? getClickColor({ color, variant: 'secondary', theme })
                : bg.active,
            borderColor: color
                ? getClickColor({ color, variant: 'secondary', theme })
                : border.active,
            color: textColor.active
        },
        // Default disabled styles
        '&:disabled': {
            bg: bg.disabled,
            borderColor: border.disabled,
            color: textColor.disabled
        }
    });
}}

  ${({ theme: { borderWidths, buttons }, color }) => {
    const { primary, secondary, tertiary } = buttons;
    return variant({
        variants: {
            primary: {
                bg: color || primary.bg.default,
                borderColor: color
                    ? themeGet('colors.' + color, color)({ theme })
                    : primary.border.default,
                color: primary.color.default,
                // Primary hover styles
                '&:hover': {
                    bg: color
                        ? getHoverColor({ color, variant: 'primary', theme })
                        : primary.bg.hover,
                    borderColor: color
                        ? getHoverColor({
                            color,
                            variant: 'primary',
                            theme
                        })
                        : primary.border.hover,
                    color: primary.color.hover
                },
                // Primary active styles
                '&:active': {
                    bg: color
                        ? getClickColor({ color, variant: 'primary', theme })
                        : primary.bg.active,
                    borderColor: color
                        ? getClickColor({
                            color,
                            variant: 'primary',
                            theme
                        })
                        : primary.border.active,
                    color: primary.color.active
                },
                // Primary disabled styles
                '&:disabled': {
                    bg: primary.bg.disabled,
                    borderColor: primary.border.disabled,
                    color: primary.color.disabled
                }
            },
            secondary: {
                bg: secondary.bg.default,
                borderColor: color
                    ? themeGet('colors.' + color, color)({ theme })
                    : secondary.border.default,
                color: color || secondary.color.default,
                // Secondary hover styles
                '&:hover': {
                    bg: color
                        ? getHoverColor({ color, variant: 'secondary', theme })
                        : secondary.bg.hover,
                    borderColor: color
                        ? getHoverColor({
                            color,
                            variant: 'secondary',
                            theme
                        })
                        : secondary.border.hover,
                    color: secondary.color.hover
                },
                // Secondary active styles
                '&:active': {
                    bg: color
                        ? getClickColor({ color, variant: 'secondary', theme })
                        : secondary.bg.active,
                    borderColor: color
                        ? getClickColor({
                            color,
                            variant: 'secondary',
                            theme
                        })
                        : secondary.border.active,
                    color: secondary.color.active
                },
                // Secondary disabled styles
                '&:disabled': {
                    bg: secondary.bg.disabled,
                    borderColor: secondary.border.disabled,
                    color: secondary.color.disabled
                }
            },
            tertiary: {
                bg: tertiary.bg.default,
                borderColor: tertiary.border.default,
                color: color || tertiary.color.default,
                // Tertiary hover styles
                '&:hover': {
                    bg: color
                        ? getHoverColor({ color, variant: 'tertiary', theme })
                        : tertiary.bg.hover,
                    borderColor: color
                        ? getHoverColor({
                            color,
                            variant: 'tertiary',
                            theme
                        })
                        : tertiary.border.hover,
                    color: color || tertiary.color.hover
                },
                // Tertiary active styles
                '&:active': {
                    bg: color
                        ? getClickColor({ color, variant: 'tertiary', theme })
                        : tertiary.bg.active,
                    borderColor: color
                        ? getClickColor({
                            color,
                            variant: 'tertiary',
                            theme
                        })
                        : tertiary.border.active,
                    color: color || tertiary.color.active
                },
                // Tertiary disabled styles
                '&:disabled': {
                    bg: tertiary.bg.disabled,
                    borderColor: tertiary.border.disabled,
                    color: tertiary.color.disabled
                }
            }
        }
    });
}}
  ${buttonStyles}
  &:hover, &:active, &:disabled {
    ${buttonStyles}
  }
  ${compose(typography, space, border, alignSelf)}
`;
Button.defaultProps = { theme };
export default Button;
//# sourceMappingURL=Button.js.map